import { IMG } from "./images";

export const blogs = [
  {
    slug: "how-much-does-artificial-grass-cost-in-scotland",
    title: "How Much Does Artificial Grass Cost in Scotland? (2026 Guide)",
    excerpt: "A complete breakdown of artificial grass costs in Scotland — supply only, fully fitted prices, what affects pricing and what to budget per square metre.",
    cover: IMG.blog1,
    category: "Pricing Guides",
    readTime: 9,
    date: "2026-01-12",
    body: `If you're considering artificial grass for your Scottish garden, the first question is almost always the same: <strong>how much does it actually cost?</strong> In this guide we break down 2026 pricing in detail — supply only, fully fitted, what affects the final price and how Scotland's specific weather and soil conditions impact your budget.

<h2>Quick Answer: Artificial Grass Costs in Scotland</h2>
A typical fully fitted artificial grass installation in Scotland costs <strong>£65–£95 per square metre</strong>. Supply-only premium grass starts around £15/m² for budget options and climbs to £35/m² for top-tier pet-friendly multi-tone products. The biggest variable is ground preparation — and in Scotland, ground prep is where corners get cut.

<h2>What's Included in a Quality Install?</h2>
A properly installed artificial lawn in Scotland needs <em>all</em> of the following:
<ul>
<li>Existing turf or surface lifted and disposed of</li>
<li>Excavation to 75–100mm</li>
<li>MOT Type 1 hardcore sub-base, compacted in layers</li>
<li>25mm laying course of sharp sand or granite dust, levelled</li>
<li>Heavy-duty weed membrane with overlapped joins</li>
<li>Premium grass with permeable backing</li>
<li>Galvanised U-pins or perimeter timber edging</li>
<li>Kiln-dried silica sand brushed into the pile</li>
</ul>
If any of those are missing from your quote, the install will fail — usually within 18 months as Scottish frost heaves the surface.

<h2>Premium vs Budget Grass</h2>
The grass itself ranges from <strong>£15/m²</strong> (thin, single-tone, suitable for show areas only) to <strong>£35/m²</strong> (40mm pile, multi-tone, pet-friendly with antibacterial backing). For most Central Belt homes we recommend the £22–£28/m² band — a 30–35mm pile with realistic colour blend and a 10-year warranty.

<h2>Real Quote Examples</h2>
<ul>
<li><strong>Small back garden (25m²) Edinburgh:</strong> £1,700–£2,300 fully fitted</li>
<li><strong>Family garden (60m²) Falkirk:</strong> £3,900–£5,500 fully fitted</li>
<li><strong>Large garden (120m²) Glasgow:</strong> £7,500–£10,800 fully fitted</li>
</ul>

<h2>What Cheap Quotes Miss</h2>
We're regularly called out to fix botched installs. The common shortcuts are: laying directly on existing turf (it dies and dips), no sub-base (puddles for life), no weed membrane (grass through every join), and unbranded grass with no warranty. A 30% cheaper quote that fails in 2 years is the most expensive grass you'll ever buy.

<h2>Is It Worth It?</h2>
For Scottish families — yes. Real lawns in our climate spend half the year muddy, patchy or unusable. Artificial grass gives you a clean, dry, dog-friendly garden 365 days a year, with zero mowing, watering or fertilising. Most installs pay back in saved time and lawncare costs within 5–7 years.

<h2>Get a Free Quote</h2>
Last Cut Landscaping offers free, no-obligation quotes across the Central Belt of Scotland. We measure precisely, recommend honestly and back every install with a 10-year warranty.`,
  },
  {
    slug: "best-landscaping-ideas-for-scottish-weather",
    title: "Best Landscaping Ideas for Scottish Weather",
    excerpt: "Practical, beautiful landscaping ideas designed for Scotland's wet, windy climate — what works, what doesn't, and how to build a garden you'll actually use.",
    cover: IMG.blog2,
    category: "Design",
    readTime: 11,
    date: "2026-01-08",
    body: `Designing a garden for Scottish weather requires accepting one truth: you'll have far more wet days than dry. Here are the landscaping ideas that actually work in our climate — and the trends to avoid.

<h2>1. Hard-Wearing Hardscape First</h2>
The single biggest upgrade for a Scottish garden is generous hard surfaces. Patios, paths and decking give you usable space when the lawn is too wet to walk on. Aim for at least 25–30% of your garden as hardscape.

<h2>2. Porcelain Over Sandstone for High-Wet Areas</h2>
Indian sandstone is beautiful but absorbs water. Porcelain is frost-proof, non-slip and stays clean — ideal for shaded or north-facing patios in Scotland.

<h2>3. Artificial Grass for Family Lawns</h2>
Real lawns struggle in Scotland's wet summers. For families with kids or dogs, premium artificial grass is the highest-impact upgrade you can make.

<h2>4. Layered Planting with Year-Round Structure</h2>
Build planting in layers: evergreen structure (box, yew, viburnum) + perennials + bulbs. Hostas, ferns, hydrangeas, heucheras and ornamental grasses all thrive in Scottish gardens.

<h2>5. Proper Drainage Built In</h2>
Every hardscape needs falls, channels or soakaways. We've fixed countless gardens where flooding came from missing drainage — solve it at design stage.

<h2>6. Decking with Grooves and Cover</h2>
Composite decking with grooves stays grip-safe in the wet. Add a pergola or covered seating area and you can sit out in light rain — a game-changer for Scottish usability.

<h2>7. Shelter from Wind</h2>
Scotland's wind matters more than people realise. Hedging, slatted fencing and pergolas create the calm microclimates that make a garden enjoyable.

<h2>8. Outdoor Lighting</h2>
Scottish winters are dark. Layered low-voltage lighting transforms a garden into something you actually look at from October through March.`,
  },
  {
    slug: "artificial-grass-vs-real-grass-in-scotland",
    title: "Artificial Grass vs Real Grass in Scotland",
    excerpt: "An honest comparison of artificial and real lawns in Scotland's climate — cost, maintenance, longevity, drainage, pets, kids and environmental impact.",
    cover: IMG.blog3,
    category: "Comparisons",
    readTime: 8,
    date: "2026-01-04",
    body: `The artificial vs real grass debate is loudest in countries with great weather. In Scotland, the conversation tilts heavily one way — but it's not unanimous. Here's the honest breakdown.

<h2>Cost Over 15 Years</h2>
<strong>Real lawn:</strong> £200–£400 install + £150/year maintenance = £2,450–£2,650 over 15 years.<br/>
<strong>Artificial:</strong> £4,500–£6,500 install + minimal maintenance = £4,700 over 15 years.<br/>
Artificial is roughly 2x the cost over 15 years — but you get back ~200 hours of mowing time.

<h2>Usability in Scottish Weather</h2>
Real grass: unusable in winter, often muddy from October to April. Artificial: usable 365 days a year.

<h2>Pets and Kids</h2>
Real grass with dogs becomes a mud pit. Artificial drains, rinses clean and stays consistent.

<h2>Environmental Considerations</h2>
Real lawn supports more biodiversity but consumes water, fuel (mowers) and fertiliser. Modern artificial grass is recyclable but plastic-based. For pollinator support, a mixed planting border beside artificial grass is the best compromise.

<h2>Resale Value</h2>
Premium artificial grass adds value in family-target homes. Avoid budget grass — it visibly ages and can deter buyers.

<h2>Our Verdict</h2>
For Scottish families with kids or pets: artificial wins. For wildlife-focused gardeners with time to maintain: real wins. For everyone in between: a small artificial lawn plus planted borders is the sweet spot.`,
  },
  {
    slug: "best-patio-materials-for-scotland",
    title: "Best Patio Materials for Scotland",
    excerpt: "Sandstone, porcelain, granite, slate or concrete? Our complete guide to choosing the right patio material for Scottish weather and budgets.",
    cover: IMG.blog4,
    category: "Materials",
    readTime: 10,
    date: "2026-01-02",
    body: `<h2>Indian Sandstone</h2>
Beautiful, characterful, mid-priced. The most popular patio material in Scotland. Needs sealing every 3–5 years to resist algae in shaded spots.

<h2>Porcelain</h2>
Premium, frost-proof, virtually maintenance-free. The right answer for north-facing or heavily shaded patios. 30% more expensive than sandstone but lasts indefinitely without sealing.

<h2>Granite</h2>
Hardest of all natural stones. Beautiful texture, excellent grip in the wet. Higher cost.

<h2>Slate</h2>
Stunning dark tones but slippery when wet — best for accent areas or undercover use.

<h2>Concrete Pavers</h2>
Cheapest option, fine quality from major brands, but lacks the natural character of stone.

<h2>Our Recommendation</h2>
For most Scottish gardens: <strong>porcelain</strong> for primary use areas, <strong>Indian sandstone</strong> for budget-conscious large patios with full sealing schedule.`,
  },
  {
    slug: "garden-drainage-solutions-for-wet-weather",
    title: "Garden Drainage Solutions for Scotland's Wet Weather",
    excerpt: "If your garden is waterlogged every winter, you need a drainage strategy. We cover French drains, soakaways, aeration and SuDS solutions.",
    cover: IMG.blog5,
    category: "Drainage",
    readTime: 9,
    date: "2025-12-28",
    body: `<h2>Why Scottish Gardens Flood</h2>
Most Central Belt gardens sit on clay subsoil with little natural drainage. Combine that with our annual rainfall and you get waterlogged lawns, soggy patios and structural moisture problems.

<h2>Diagnosing the Problem</h2>
Pool standing for >24 hours after rain = compacted clay. Pool through summer = a high water table or buried clay layer. Different problems, different solutions.

<h2>Solutions</h2>
<ul>
<li><strong>Lawn aeration:</strong> hollow-tine aeration with sand top-dressing solves surface compaction.</li>
<li><strong>French drains:</strong> perforated pipe in gravel-filled trench, discharging to a soakaway or surface water drain.</li>
<li><strong>Soakaway:</strong> a gravel-filled pit (or modular crate) that disperses water into surrounding soil.</li>
<li><strong>Channel drains:</strong> linear gratings at the edges of patios and driveways.</li>
<li><strong>Land drains:</strong> for larger sites, a herringbone of pipes across the lawn.</li>
</ul>

<h2>Costs</h2>
Soakaways from £600. French drains from £80/linear metre. Full system £1,500–£4,500.`,
  },
  {
    slug: "low-maintenance-garden-ideas-scotland",
    title: "Low Maintenance Garden Ideas for Scotland",
    excerpt: "A beautiful garden that takes 30 minutes a week — design principles, plant choices and hardscape strategy for low-maintenance Scottish gardens.",
    cover: IMG.blog6,
    category: "Design",
    readTime: 8,
    date: "2025-12-22",
    body: `<h2>Principles of Low Maintenance</h2>
Three rules: (1) less lawn, (2) more hardscape, (3) shrubs over annuals.

<h2>Replace Lawn with Hardscape or Artificial Grass</h2>
The single biggest time saver. A weekly mow becomes a monthly sweep.

<h2>Shrub-Heavy Planting</h2>
Evergreen shrubs that need pruning once a year: viburnum, photinia, choisya, hebe, box, yew. Add ornamental grasses for movement.

<h2>Mulch Heavily</h2>
75mm of bark mulch on every border suppresses weeds, retains moisture and feeds the soil. Top up annually.

<h2>Automated Irrigation</h2>
A drip-line through borders takes one weekend to install and removes summer watering entirely.

<h2>The Result</h2>
A garden that looks great, costs little to maintain, and gives you time to actually use it.`,
  },
  {
    slug: "dog-friendly-gardens-scotland",
    title: "Dog Friendly Gardens in Scotland",
    excerpt: "From muddy paws to dug-up lawns, designing a garden that works for your dog in Scottish weather. Surfaces, planting, fencing and play.",
    cover: IMG.blog7,
    category: "Family Gardens",
    readTime: 8,
    date: "2025-12-15",
    body: `<h2>The Surface Question</h2>
Real lawn + Scottish weather + dog = mud. The best solutions are pet-friendly artificial grass (drains, rinses clean, no digging), bark chip play areas, or hard-paved zones with surrounding planted borders.

<h2>Avoid These Plants</h2>
Foxglove, lily, daffodil bulbs, yew and rhododendron are all toxic to dogs. Skip them.

<h2>Safe Planting</h2>
Roses, lavender, hardy geraniums, ornamental grasses, hebes and most herbs are dog-safe.

<h2>Fencing</h2>
1.8m closed-board fencing with no gaps prevents most escapes. Add a small concrete kicker board if you have a digger.

<h2>Dedicated Toilet Area</h2>
A pea-gravel or bark area trained as the toilet spot saves the rest of the garden.

<h2>Cooling and Shade</h2>
Even Scottish summers have hot days. Ensure shaded areas, water access and avoid dark hardscape that holds heat.`,
  },
  {
    slug: "composite-decking-vs-wood-in-scotland",
    title: "Composite Decking vs Wood in Scotland",
    excerpt: "Honest comparison of composite and timber decking in Scottish conditions — costs, longevity, maintenance, grip and the verdict for our climate.",
    cover: IMG.blog8,
    category: "Comparisons",
    readTime: 9,
    date: "2025-12-10",
    body: `<h2>Upfront Cost</h2>
Treated softwood: £45–£65/m² supplied and fitted. Composite: £110–£160/m² supplied and fitted.

<h2>Lifetime Cost</h2>
Softwood needs annual oiling (£200–£400 in product + 6 hours of labour). Over 20 years, the totals roughly converge.

<h2>Longevity</h2>
Quality softwood: 12–18 years with maintenance. Composite: 25+ years with no maintenance.

<h2>Grip in Scottish Rain</h2>
This matters. Grooved composite stays grippy. Smooth softwood gets slippery with algae if not cleaned regularly.

<h2>Appearance Over Time</h2>
Softwood weathers grey if untreated. Composite holds its colour for 15+ years.

<h2>Heat in Summer</h2>
Composite can get hotter underfoot in direct sun. Lighter colours minimise this.

<h2>Our Verdict</h2>
For Scotland's wet climate and busy lifestyles: <strong>composite wins</strong>. For traditional looks and budget priorities: softwood remains a valid choice with annual care.`,
  },
];

export const findBlog = (slug) => blogs.find((b) => b.slug === slug);
